import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  ArrowRight,
  Spinner,
  CalendarCheck,
  CreditCard,
  ClipboardText,
  Check,
} from "@phosphor-icons/react";
import axios from "axios";
import { getTrackingData } from "@/lib/tracking";
import { postToGHL, ghlKeyForIntake, buildGhlPayload } from "@/lib/ghl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "bg-[#1A1A1A]/80 backdrop-blur-sm border border-white/10 rounded-md px-4 py-3 text-[#F2EFE9] placeholder:text-[#8C8C8C]/60 font-body text-sm focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none w-full transition-all";
const selectClass = `${inputClass} appearance-none`;

const YOUTH_CALENDLY_URL =
  "https://calendly.com/3sm-training/3sm-youth-readiness?hide_event_type_details=1&hide_gdpr_banner=1&background_color=000000&text_color=ffffff&primary_color=D4AF37";

const COACHING_CALENDLY_URL =
  "https://calendly.com/3sm-training/private-coaching?background_color=000000&text_color=ffffff&primary_color=D4AF37";

const WORKSHOP_CALENDLY_URL =
  "https://calendly.com/3sm-training/small-group-session?background_color=000000&text_color=ffffff&primary_color=D4AF37";

// Step configurations per program
const STEP_CONFIG = {
  coaching: {
    steps: ["Application", "Package & Payment", "Schedule"],
    icons: [ClipboardText, CreditCard, CalendarCheck],
  },
  youth: {
    steps: ["Enrollment", "Package & Payment", "Schedule"],
    icons: [ClipboardText, CreditCard, CalendarCheck],
  },
  workshop: {
    steps: ["Request", "Schedule"],
    icons: [ClipboardText, CalendarCheck],
  },
  executive: {
    steps: ["Inquiry", "Review", "Confirmation"],
    icons: [ClipboardText, ClipboardText, CheckCircle],
  },
};

// Post-submit Calendly embed config (workshop only — coaching now goes through payment first)
const CALENDLY_EMBED = {
  workshop: {
    url: WORKSHOP_CALENDLY_URL,
    title: "Your spot is almost locked in.",
    body: "Choose a time below to confirm.",
  },
};

// Post-payment Calendly embed config (coaching + youth after Stripe)
const POST_PAYMENT_CALENDLY = {
  coaching: {
    url: COACHING_CALENDLY_URL,
    title: "You took the first step.",
    body: "Coach D will be in touch within 24 hours.",
  },
  youth: {
    url: YOUTH_CALENDLY_URL,
    title: "We can\u2019t wait to work with your family.",
    body: "Pick a time that works for you.",
  },
};

// Build Calendly URL with pre-filled name & email from form data
function prefillCalendly(baseUrl, name, email) {
  let url = baseUrl;
  if (name) url += `&name=${encodeURIComponent(name)}`;
  if (email) url += `&email=${encodeURIComponent(email)}`;
  return url;
}

const FORMS = {
  coaching: {
    title: "Private Coaching Application",
    subtitle:
      "Tell us about your goals so we can match you with the right program.",
    fields: [
      { name: "name", label: "Full Name", type: "text", required: true },
      { name: "email", label: "Email Address", type: "email", required: true },
      { name: "phone", label: "Phone Number", type: "tel", required: true },
      {
        name: "experience_level",
        label: "Experience Level",
        type: "select",
        required: true,
        options: [
          { value: "", label: "Select your level" },
          { value: "none", label: "No prior training" },
          { value: "beginner", label: "Beginner (< 1 year)" },
          { value: "intermediate", label: "Intermediate (1-3 years)" },
          { value: "advanced", label: "Advanced (3+ years)" },
        ],
      },
      {
        name: "goals",
        label: "What are your main goals?",
        type: "textarea",
        required: true,
        placeholder: "Self-defense, fitness, confidence, competition prep...",
      },
      {
        name: "preferred_schedule",
        label: "Preferred Schedule",
        type: "select",
        required: true,
        options: [
          { value: "", label: "When works best?" },
          { value: "mornings", label: "Mornings (6am-12pm)" },
          { value: "afternoons", label: "Afternoons (12pm-5pm)" },
          { value: "evenings", label: "Evenings (5pm-8pm)" },
          { value: "weekends", label: "Weekends" },
          { value: "flexible", label: "Flexible" },
        ],
      },
      {
        name: "how_heard",
        label: "How did you hear about us?",
        type: "text",
        required: false,
        placeholder: "Google, referral, Instagram...",
      },
      {
        name: "what_prompted",
        label: "What prompted you to reach out now?",
        type: "textarea",
        required: false,
        placeholder: "A recent event, a goal you've been putting off, someone recommended us...",
      },
    ],
  },
  youth: {
    title: "Youth Readiness Enrollment",
    subtitle: "Tell us about your child so we can find the right fit.",
    fields: [
      { name: "parent_name", label: "Parent/Guardian Name", type: "text", required: true },
      { name: "email", label: "Email Address", type: "email", required: true },
      { name: "phone", label: "Phone Number", type: "tel", required: true },
      { name: "child_name", label: "Child's Name", type: "text", required: true },
      {
        name: "child_age",
        label: "Child's Age",
        type: "select",
        required: true,
        options: [
          { value: "", label: "Select age" },
          ...Array.from({ length: 13 }, (_, i) => ({
            value: `${i + 5}`,
            label: `${i + 5} years old`,
          })),
        ],
      },
      {
        name: "goals",
        label: "What do you hope your child gains?",
        type: "textarea",
        required: true,
        placeholder: "Confidence, discipline, coordination, self-defense awareness...",
      },
      {
        name: "special_concerns",
        label: "Any special concerns or accommodations?",
        type: "textarea",
        required: false,
        placeholder: "Leave blank if none",
      },
      {
        name: "preferred_schedule",
        label: "Preferred Schedule",
        type: "select",
        required: false,
        options: [
          { value: "", label: "When works best?" },
          { value: "after-school", label: "After School (3pm-5pm)" },
          { value: "mornings", label: "Mornings" },
          { value: "weekends", label: "Weekends" },
          { value: "flexible", label: "Flexible" },
        ],
      },
    ],
  },
  workshop: {
    title: "Workshop Request",
    subtitle: "Tell us about your group and we'll get back to you with options.",
    fields: [
      { name: "name", label: "Your Name", type: "text", required: true },
      { name: "email", label: "Email Address", type: "email", required: true },
      { name: "phone", label: "Phone Number", type: "tel", required: true },
      {
        name: "organization",
        label: "Organization (if applicable)",
        type: "text",
        required: false,
        placeholder: "Company, nonprofit, gym...",
      },
      {
        name: "group_size",
        label: "Estimated Group Size",
        type: "select",
        required: true,
        options: [
          { value: "", label: "How many people?" },
          { value: "1", label: "Just me" },
          { value: "5", label: "2-5 people" },
          { value: "10", label: "6-10 people" },
          { value: "20", label: "11-20 people" },
          { value: "30", label: "20+ people" },
        ],
      },
      {
        name: "workshop_type",
        label: "Workshop Interest",
        type: "select",
        required: true,
        options: [
          { value: "", label: "Select type" },
          { value: "womens", label: "Women's Self-Defense" },
          { value: "coed", label: "Co-Ed Self-Defense" },
          { value: "corporate", label: "Corporate / Team Training" },
          { value: "nonprofit", label: "Nonprofit / Community" },
          { value: "custom", label: "Custom / Other" },
        ],
      },
      {
        name: "preferred_dates",
        label: "Preferred Date Range",
        type: "text",
        required: false,
        placeholder: "e.g. March-April, weekends preferred...",
      },
      {
        name: "additional_info",
        label: "Anything else we should know?",
        type: "textarea",
        required: false,
      },
    ],
  },
  executive: {
    title: "Executive Composure Inquiry",
    subtitle: "Tell us about your team's needs and we'll prepare a tailored proposal.",
    fields: [
      { name: "name", label: "Your Name", type: "text", required: true },
      { name: "email", label: "Email Address", type: "email", required: true },
      { name: "phone", label: "Phone Number", type: "tel", required: true },
      { name: "company", label: "Company / Organization", type: "text", required: true },
      { name: "role_title", label: "Your Role/Title", type: "text", required: false },
      {
        name: "team_size",
        label: "Team Size",
        type: "select",
        required: true,
        options: [
          { value: "", label: "How many participants?" },
          { value: "5", label: "2-5" },
          { value: "10", label: "6-10" },
          { value: "20", label: "11-20" },
          { value: "50", label: "20-50" },
          { value: "100", label: "50+" },
        ],
      },
      {
        name: "training_need",
        label: "What's driving this training need?",
        type: "textarea",
        required: true,
        placeholder: "Team development, workplace safety, leadership resilience, misconduct prevention...",
      },
      {
        name: "preferred_timeline",
        label: "Preferred Timeline",
        type: "select",
        required: false,
        options: [
          { value: "", label: "When do you need this?" },
          { value: "asap", label: "As soon as possible" },
          { value: "1month", label: "Within 1 month" },
          { value: "3months", label: "Within 3 months" },
          { value: "exploring", label: "Just exploring options" },
        ],
      },
      {
        name: "budget_range",
        label: "Budget Range",
        type: "select",
        required: false,
        options: [
          { value: "", label: "What's your approximate budget?" },
          { value: "under-5k", label: "Under $5,000" },
          { value: "5k-10k", label: "$5,000 - $10,000" },
          { value: "10k-25k", label: "$10,000 - $25,000" },
          { value: "25k-50k", label: "$25,000 - $50,000" },
          { value: "50k-plus", label: "$50,000+" },
          { value: "unsure", label: "Not sure yet" },
        ],
      },
    ],
  },
};

/* ─── Progress Stepper ─── */
function ProgressStepper({ steps, icons, currentStep }) {
  return (
    <div className="flex items-center justify-center mb-10 px-2" data-testid="progress-stepper">
      {steps.map((label, i) => {
        const Icon = icons[i];
        const isActive = i === currentStep;
        const isDone = i < currentStep;
        return (
          <div key={label} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                data-testid={`step-indicator-${i}`}
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                  isDone
                    ? "bg-[#D4AF37] border-[#D4AF37]"
                    : isActive
                    ? "border-[#D4AF37] bg-[#D4AF37]/15"
                    : "border-white/15 bg-transparent"
                }`}
              >
                {isDone ? (
                  <Check size={18} weight="bold" className="text-[#050505]" />
                ) : (
                  <Icon
                    size={18}
                    weight={isActive ? "fill" : "regular"}
                    className={isActive ? "text-[#D4AF37]" : "text-white/30"}
                  />
                )}
              </div>
              <span
                className={`mt-2 text-[10px] sm:text-xs font-body tracking-wider uppercase whitespace-nowrap ${
                  isDone
                    ? "text-[#D4AF37]"
                    : isActive
                    ? "text-[#F2EFE9]"
                    : "text-white/25"
                }`}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`w-10 sm:w-16 lg:w-24 h-px mx-2 sm:mx-3 mb-6 transition-colors duration-300 ${
                  i < currentStep ? "bg-[#D4AF37]" : "bg-white/10"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Calendly Inline Embed ─── */
function CalendlyEmbed({ url }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const loadAndInit = () => {
      if (containerRef.current && window.Calendly) {
        // Clear any previous widget content
        containerRef.current.innerHTML = "";
        window.Calendly.initInlineWidget({
          url,
          parentElement: containerRef.current,
        });
      }
    };

    // If Calendly is already loaded, init immediately
    if (window.Calendly) {
      loadAndInit();
      return;
    }

    // Otherwise load the script then init
    const existing = document.querySelector('script[src*="assets.calendly.com/assets/external/widget.js"]');
    if (existing) {
      // Script tag exists but hasn't finished loading yet
      existing.addEventListener("load", loadAndInit);
      return () => existing.removeEventListener("load", loadAndInit);
    }

    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    script.onload = loadAndInit;
    document.head.appendChild(script);
  }, [url]);

  return (
    <div
      ref={containerRef}
      data-testid="calendly-embed"
      className="w-full rounded-lg overflow-hidden"
      style={{ minWidth: "320px", height: "700px" }}
    />
  );
}

/* ─── Package Selection Card ─── */
function PackageCard({ pkg, selected, onSelect }) {
  const isSelected = selected === pkg.key;
  return (
    <button
      type="button"
      data-testid={`package-${pkg.key}`}
      onClick={() => onSelect(pkg.key)}
      className={`w-full text-left p-4 rounded-lg border transition-all duration-200 ${
        isSelected
          ? "border-[#D4AF37] bg-[#D4AF37]/10"
          : "border-white/10 bg-[#1A1A1A]/50 hover:border-white/20"
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p
            className={`font-heading font-bold text-sm ${
              isSelected ? "text-[#D4AF37]" : "text-[#F2EFE9]"
            }`}
          >
            {pkg.name}
          </p>
          {pkg.duration > 0 && (
            <p className="text-[#8C8C8C] text-xs font-body mt-0.5">
              {pkg.duration} min per session
            </p>
          )}
        </div>
        <div className="text-right">
          <p
            className={`font-heading font-black text-lg ${
              isSelected ? "text-[#D4AF37]" : "text-[#F2EFE9]"
            }`}
          >
            ${pkg.price}
          </p>
        </div>
      </div>
      <div
        className={`mt-2 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
          isSelected ? "border-[#D4AF37]" : "border-white/20"
        }`}
      >
        {isSelected && <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />}
      </div>
    </button>
  );
}

/* ─── Main Component ─── */
export default function IntakeForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const type = searchParams.get("type") || "coaching";
  const stepParam = searchParams.get("step");
  const sessionIdParam = searchParams.get("session_id");
  const config = FORMS[type];
  const stepConfig = STEP_CONFIG[type];

  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [intakeResult, setIntakeResult] = useState(null);
  const [intakeId, setIntakeId] = useState("");

  // Package selection (youth only)
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [packagesLoading, setPackagesLoading] = useState(false);

  // Payment (youth only)
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollingPayment, setPollingPayment] = useState(false);

  // Youth scheduling countdown
  const [countdown, setCountdown] = useState(5);
  const redirectAttempted = useRef(false);

  // Detect return from Stripe (youth payment flow)
  useEffect(() => {
    if (stepParam === "payment-success" && sessionIdParam) {
      setCurrentStep(2);
      setPollingPayment(true);
      pollPaymentStatus(sessionIdParam, 0);
    } else if (stepParam === "payment-cancel") {
      setCurrentStep(1);
      setError("Payment was cancelled. You can try again or select a different package.");
    }
  }, []);

  // Load packages when entering step 2 (youth + coaching)
  useEffect(() => {
    if (currentStep === 1 && (type === "youth" || type === "coaching")) {
      loadPackages();
    }
  }, [currentStep, type]);

  // Youth Calendly redirect after payment confirmed
  useEffect(() => {
    if (type !== "youth" || currentStep !== 2 || paymentStatus !== "paid") return;
    if (redirectAttempted.current) return;

    const youthCalendlyPrefilled = prefillCalendly(YOUTH_CALENDLY_URL, form.parent_name, form.email);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          redirectAttempted.current = true;
          window.location.href = youthCalendlyPrefilled;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [type, currentStep, paymentStatus]);

  const loadPackages = async () => {
    setPackagesLoading(true);
    try {
      const { data } = await axios.get(`${API}/packages/${type}`);
      setPackages(data);
      if (data.length > 0) setSelectedPackage(data[0].key);
    } catch {
      setError("Failed to load packages.");
    } finally {
      setPackagesLoading(false);
    }
  };

  const pollPaymentStatus = useCallback(async (sid, attempts) => {
    const maxAttempts = 10;
    const pollInterval = 2500;
    if (attempts >= maxAttempts) {
      setPollingPayment(false);
      setPaymentStatus("timeout");
      return;
    }
    try {
      const { data } = await axios.get(`${API}/checkout/status/${sid}`);
      if (data.payment_status === "paid") {
        setPaymentStatus("paid");
        setPollingPayment(false);
        return;
      }
      if (data.status === "expired") {
        setPaymentStatus("expired");
        setPollingPayment(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch {
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  }, []);

  if (!config) {
    navigate("/");
    return null;
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Phone validation — at least 10 digits
    const phoneDigits = (form.phone || "").replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      setError("Please enter your phone number so we can follow up with you.");
      setLoading(false);
      return;
    }

    const tracking = getTrackingData();

    try {
      // 1. Save to backend (MongoDB + Supabase + admin records)
      const payload = { ...form, tracking };
      const { data } = await axios.post(`${API}/intake/${type}`, payload);

      // 2. POST directly to the correct GoHighLevel webhook
      const ghlKey = ghlKeyForIntake(type);
      if (ghlKey) {
        const contactName = type === "youth" ? form.parent_name : form.name;
        const message =
          type === "executive" ? form.training_need :
          type === "workshop"  ? form.additional_info :
          form.goals;
        const { tracking: _t, name: _n, parent_name: _p, email: _e, phone: _ph, ...extras } = form;
        try {
          await postToGHL(ghlKey, buildGhlPayload({
            formType: `${type}_intake`,
            name: contactName,
            email: form.email,
            phone: form.phone,
            message: message || "",
            extras: { ...extras, intake_id: data.intake_id },
            tracking,
          }));
        } catch (ghlErr) {
          // Lead is already saved server-side; log but don't block success UI.
          console.error("GoHighLevel webhook error:", ghlErr);
        }
      }

      setIntakeResult(data);
      setIntakeId(data.intake_id);
      setCurrentStep(1);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedPackage) {
      setError("Please select a package.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/checkout/create`, {
        service_key: selectedPackage,
        intake_id: intakeId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to initiate payment. Please try again.");
      setLoading(false);
    }
  };

  const updateField = (name, value) => setForm({ ...form, [name]: value });

  // Does this program use an inline Calendly embed after form submission?
  const calendlyEmbed = CALENDLY_EMBED[type];

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-white/10 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <a
            href="/"
            className="font-heading text-xl font-black tracking-tighter text-[#F2EFE9]"
          >
            3<span className="text-[#D4AF37]">SM</span>
          </a>
          <button
            data-testid="intake-back"
            onClick={() => navigate("/")}
            className="text-sm text-[#8C8C8C] hover:text-[#D4AF37] transition-colors font-body flex items-center gap-1"
          >
            <ArrowLeft size={14} /> Back to site
          </button>
        </div>
      </header>

      <div className={`mx-auto px-6 py-8 ${currentStep === 1 && calendlyEmbed ? "max-w-3xl" : "max-w-2xl"}`}>
        {/* Progress Stepper */}
        <ProgressStepper
          steps={stepConfig.steps}
          icons={stepConfig.icons}
          currentStep={currentStep}
        />

        {/* ─── STEP 1: Intake Form ─── */}
        {currentStep === 0 && (
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#F2EFE9] mb-1">
              {config.title}
            </h1>
            <p className="text-[#8C8C8C] font-body text-sm mb-8">
              {config.subtitle}
            </p>
            <form
              onSubmit={handleFormSubmit}
              data-testid="intake-form"
              className="space-y-4"
            >
              {config.fields.map((field) => (
                <div key={field.name}>
                  <label className="text-xs text-[#8C8C8C] font-body uppercase tracking-wider mb-1.5 block">
                    {field.label}
                    {field.required && (
                      <span className="text-[#D4AF37] ml-0.5">*</span>
                    )}
                  </label>
                  {field.type === "select" ? (
                    <select
                      data-testid={`intake-${field.name}`}
                      required={field.required}
                      value={form[field.name] || ""}
                      onChange={(e) => updateField(field.name, e.target.value)}
                      className={`${selectClass} ${
                        !form[field.name] ? "text-[#8C8C8C]/60" : ""
                      }`}
                    >
                      {field.options.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "textarea" ? (
                    <textarea
                      data-testid={`intake-${field.name}`}
                      required={field.required}
                      placeholder={field.placeholder || ""}
                      value={form[field.name] || ""}
                      onChange={(e) => updateField(field.name, e.target.value)}
                      rows={3}
                      className={inputClass}
                    />
                  ) : (
                    <input
                      data-testid={`intake-${field.name}`}
                      type={field.type}
                      required={field.required}
                      placeholder={field.placeholder || ""}
                      value={form[field.name] || ""}
                      onChange={(e) => updateField(field.name, e.target.value)}
                      className={inputClass}
                    />
                  )}
                </div>
              ))}

              {error && (
                <p data-testid="intake-error" className="text-red-400 text-sm font-body">
                  {error}
                </p>
              )}

              <button
                data-testid="intake-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-[#050505] font-bold px-8 py-4 rounded-md hover:bg-[#E5C158] transition-all uppercase tracking-widest text-sm disabled:opacity-50 cta-glow flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Spinner size={18} className="animate-spin" /> Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </button>
            </form>
          </div>
        )}

        {/* ─── STEP 2 (workshop): Success + Calendly Inline Embed ─── */}
        {currentStep === 1 && calendlyEmbed && (
          <div>
            <div className="text-center mb-8">
              <CheckCircle
                size={48}
                weight="duotone"
                className="text-[#D4AF37] mx-auto mb-4"
              />
              <h1
                data-testid="intake-success"
                className="font-heading text-2xl font-black text-[#F2EFE9] mb-2"
              >
                {calendlyEmbed.title}
              </h1>
              <p className="text-[#8C8C8C] font-body text-sm leading-relaxed max-w-md mx-auto">
                {calendlyEmbed.body}
              </p>
            </div>
            <CalendlyEmbed url={prefillCalendly(calendlyEmbed.url, form.name, form.email)} />
            <button
              data-testid="intake-back-home"
              onClick={() => navigate("/")}
              className="mt-8 block mx-auto text-[#8C8C8C] text-sm font-body hover:text-[#D4AF37] transition-colors"
            >
              &larr; Back to Home
            </button>
          </div>
        )}

        {/* ─── STEP 2 (coaching + youth): Package Selection & Stripe Payment ─── */}
        {currentStep === 1 && (type === "youth" || type === "coaching") && (
          <div>
            <h2 className="font-heading text-2xl font-bold text-[#F2EFE9] mb-1">
              Select Your Package
            </h2>
            <p className="text-[#8C8C8C] font-body text-sm mb-6">
              Choose the plan that fits your goals. You'll be redirected to
              secure checkout.
            </p>

            {packagesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size={28} className="animate-spin text-[#D4AF37]" />
              </div>
            ) : (
              <div className="space-y-3 mb-6" data-testid="package-list">
                {packages.map((pkg) => (
                  <PackageCard
                    key={pkg.key}
                    pkg={pkg}
                    selected={selectedPackage}
                    onSelect={setSelectedPackage}
                  />
                ))}
              </div>
            )}

            {error && (
              <p data-testid="payment-error" className="text-red-400 text-sm font-body mb-4">
                {error}
              </p>
            )}

            <button
              data-testid="pay-now-btn"
              onClick={handlePayment}
              disabled={loading || !selectedPackage}
              className="w-full bg-[#D4AF37] text-[#050505] font-bold px-8 py-4 rounded-md hover:bg-[#E5C158] transition-all uppercase tracking-widest text-sm disabled:opacity-50 cta-glow flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Spinner size={18} className="animate-spin" /> Redirecting to checkout...
                </>
              ) : (
                <>
                  <CreditCard size={18} weight="bold" /> Pay Now
                </>
              )}
            </button>

            <button
              onClick={() => setCurrentStep(0)}
              className="mt-4 block mx-auto text-[#8C8C8C] text-sm font-body hover:text-[#D4AF37] transition-colors"
            >
              &larr; Back to form
            </button>
          </div>
        )}

        {/* ─── STEP 2 (executive): Proposal Review ─── */}
        {currentStep === 1 && type === "executive" && (
          <div className="text-center py-8">
            <CheckCircle
              size={56}
              weight="duotone"
              className="text-[#D4AF37] mx-auto mb-6"
            />
            <h2
              data-testid="intake-success"
              className="font-heading text-2xl font-black text-[#F2EFE9] mb-4"
            >
              Your inquiry is in.
            </h2>
            <p className="text-[#8C8C8C] font-body text-sm leading-relaxed mb-8 max-w-sm mx-auto">
              Expect a tailored proposal within 48 hours.
            </p>
            <button
              data-testid="intake-back-home"
              onClick={() => navigate("/")}
              className="text-[#8C8C8C] text-sm font-body hover:text-[#D4AF37] transition-colors"
            >
              &larr; Back to Home
            </button>
          </div>
        )}

        {/* ─── STEP 3 (coaching + youth): Payment Confirmation + Scheduling ─── */}
        {currentStep === 2 && (type === "youth" || type === "coaching") && (
          <div className="text-center py-8">
            {pollingPayment ? (
              <div>
                <Spinner
                  size={48}
                  className="animate-spin text-[#D4AF37] mx-auto mb-6"
                />
                <h2 className="font-heading text-xl font-bold text-[#F2EFE9] mb-2">
                  Verifying your payment...
                </h2>
                <p className="text-[#8C8C8C] font-body text-sm">
                  Please wait while we confirm your transaction.
                </p>
              </div>
            ) : paymentStatus === "paid" ? (
              <div>
                <CheckCircle
                  size={56}
                  weight="duotone"
                  className="text-[#D4AF37] mx-auto mb-6"
                />
                <h2
                  data-testid="payment-success"
                  className="font-heading text-2xl font-black text-[#F2EFE9] mb-2"
                >
                  {POST_PAYMENT_CALENDLY[type]?.title || "Payment confirmed!"}
                </h2>
                <p className="text-[#8C8C8C] font-body text-sm leading-relaxed mb-6 max-w-md mx-auto">
                  {POST_PAYMENT_CALENDLY[type]?.body || ""}
                </p>

                {/* Youth: redirect to Calendly */}
                {type === "youth" && (
                  <>
                    <p
                      data-testid="youth-redirect-countdown"
                      className="text-[#D4AF37]/70 font-body text-xs mb-6"
                    >
                      Redirecting to scheduling in {countdown}s...
                    </p>
                    <a
                      href={prefillCalendly(YOUTH_CALENDLY_URL, form.parent_name, form.email)}
                      data-testid="youth-continue-scheduling"
                      className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#050505] font-bold px-8 py-4 rounded-md hover:bg-[#E5C158] transition-all uppercase tracking-widest text-sm cta-glow"
                    >
                      <CalendarCheck size={18} weight="bold" />
                      Continue to Scheduling
                    </a>
                  </>
                )}

                {/* Coaching: inline Calendly embed */}
                {type === "coaching" && (
                  <div className="mt-4 text-left">
                    <CalendlyEmbed url={prefillCalendly(COACHING_CALENDLY_URL, form.name, form.email)} />
                  </div>
                )}

                <button
                  data-testid="intake-back-home"
                  onClick={() => navigate("/")}
                  className="mt-6 block mx-auto text-[#8C8C8C] text-sm font-body hover:text-[#D4AF37] transition-colors"
                >
                  &larr; Back to Home
                </button>
              </div>
            ) : paymentStatus === "expired" || paymentStatus === "timeout" ? (
              <div>
                <h2 className="font-heading text-xl font-bold text-red-400 mb-4">
                  Payment could not be verified
                </h2>
                <p className="text-[#8C8C8C] font-body text-sm mb-6">
                  Your session may have expired. Please try again or contact us for help.
                </p>
                <button
                  onClick={() => {
                    setCurrentStep(1);
                    setPaymentStatus(null);
                    setError("");
                  }}
                  className="bg-[#D4AF37] text-[#050505] font-bold px-8 py-4 rounded-md hover:bg-[#E5C158] transition-all uppercase tracking-widest text-sm cta-glow"
                >
                  Try Again
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
